import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { combineLatest, Observable, of } from 'rxjs';
import { concatMap, map, takeUntil, tap } from 'rxjs/operators';

import { Branch, SftpConnectionResult, SftpCredentialsResult } from 'graphql/models/generated';
import { BaseComponent } from "shared/base/base.component";
import { StoreService } from "shared/services/store.service";
import { PasswordValidationService } from "shared/services/password-validation.service";
import { PermissionsService } from "shared/services/permissions.service";
import { SettingsService } from "shared/services/settings.service";

@Component({
  selector: "app-branch-sftp-settings-form",
  templateUrl: "./sftp-settings-form.component.html",
  styleUrls: ["./sftp-settings-form.component.scss"],
})
export class BranchSftpSettingsFormComponent extends BaseComponent implements OnInit {

  form!: UntypedFormGroup;

  branch!: Branch;

  sftpSettings!: SftpCredentialsResult;

  formReady = false;

  canSubmit = false;

  canValidate = false;

  canUpdateBranch!: boolean;

  isSuperAdmin!: boolean;

  isDealerSuperAdmin!: boolean;

  validateMessage = '';
  validateMessageClass = '';

  constructor(
    private fb: UntypedFormBuilder,
    private storeService: StoreService,
    private passwordValidationService: PasswordValidationService,
    private permissionsService: PermissionsService,
    private settingsService: SettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.canUpdateBranch = this.permissionsService.canUpdateBranch();
    this.storeService.setFormReady(this.formReady);

    combineLatest([
      this.storeService.isCurrentAccountDealer(),
      this.storeService.isSuperAdmin(),
      this.storeService.getCurrentAccountBranch(),
      this.storeService.getCurrentSftp()
    ]).subscribe(
      ([isDealer, isSuperAdmin, branch, sftpCredential]: [boolean, boolean, Branch, SftpCredentialsResult]) => {
        this.isSuperAdmin = isSuperAdmin;
        this.isDealerSuperAdmin = isDealer && isSuperAdmin;
        this.branch = branch;
        this.sftpSettings = sftpCredential;

        this.setupForm();
      }
    );


  }

  setupForm(): void {

    this.form = this.fb.group(
      {
        branchId: this?.sftpSettings?.branchId || null,
        host: [this?.sftpSettings?.host || "", [Validators.required]],
        port: [
          (this?.sftpSettings?.port ? String(this.sftpSettings.port) : ""),
          [Validators.required]
        ],
        remotePath: [this?.sftpSettings?.remotePath || ""],
        username: [this?.sftpSettings?.username || "", [Validators.required]],
        password: ["", [
          Validators.required,
          this.passwordValidationService.atLeastOneNumber,
          this.passwordValidationService.atLeastOneLowerCase,
          this.passwordValidationService.atLeastOneUpperCase,
          this.passwordValidationService.atLeastOneSpecialCharacter,
          this.passwordValidationService.minLength,
          this.passwordValidationService.noWhiteSpace
        ]],
        applyForAllBranches2: false,
        valid: [null, [Validators.required]]
      },
      { updateOn: "change" }
    );

    this.formReady = true;
    this.storeService.setFormReady(this.formReady);

    if (this.isSuperAdmin && this.canUpdateBranch) {
      this.detectFormChanges();
    } else {
      this.form.disable({ emitEvent: false });
    }
  }

  private detectFormChanges(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
      () => {
        this.canValidate = this.canValidateConnection();
        this.canSubmit = this.form.valid;
      },
      (err: any) => {
        console.info(err);
      }
    );
  }

  onSubmit(): Observable<any> {
    if (this.form.valid) {
      let formValue = this.form.value;
      const applyForAllBranches = this.isDealerSuperAdmin && this.form.value.applyForAllBranches2;
      let branchIds$: Observable<string[]>;

      if (applyForAllBranches) {
        branchIds$ = this.settingsService.getBranches()
          .pipe(
            map((branches: Branch[]) => branches.map((branch) => branch.id) as string[])
          );
      } else {
        branchIds$ = of([this.branch.id]);
      }

      branchIds$
        .pipe(
          concatMap((branchIds: string[]) =>
            of(...branchIds).pipe(
              concatMap((branchId: string) =>
                this.settingsService.setSftpCredentials(formValue, branchId).pipe(
                  tap((data) => {
                    if (branchId === this.branch.id) {
                      this.storeService.setCurrentSftp(data);
                    }
                  })
                )
              )
            )
          )
        ).subscribe({});

    }

    return of({});
  }

  validateConnection(): void {
    this.validateMessage = '';
    this.validateMessageClass = '';

    const { host, port, username, password } = this.form.value;
    const remotePath = this.form.value.remotePath || '/';

    this.settingsService.validateSftpConnection(
      { host, port, username, password, remotePath }
    ).subscribe((res: SftpConnectionResult) => {
      this.form.controls['valid'].setValue(res.status || null);
      this.validateMessage = res.message;
      this.validateMessageClass = (res.status ? 'valid' : 'invalid');
    });

  }

  canValidateConnection(): boolean {
    const hostCtrl = this.form.get('host') as AbstractControl;
    const postCtrl = this.form.get('port') as AbstractControl;
    const usernameCtrl = this.form.get('username') as AbstractControl;
    const passwordCtrl = this.form.get('password') as AbstractControl;

    return (hostCtrl.valid && postCtrl.valid && usernameCtrl.valid && passwordCtrl.valid);
  }

}
